import { useEffect, useRef } from 'react';

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus management for custom (non-Radix) overlays.
 * While `isOpen`, focus is moved into the container, Tab is cycled inside it and
 * Escape closes. On close, focus returns to the element that opened the overlay.
 */
export function useFocusTrap(isOpen, onClose) {
  const containerRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      // Restore focus to the trigger once the overlay closes.
      const previous = lastFocusedRef.current;
      lastFocusedRef.current = null;
      if (previous && document.contains(previous)) previous.focus();
      return;
    }

    lastFocusedRef.current = document.activeElement;
    const container = containerRef.current;
    if (!container) return;

    const nodes = () => Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
    const first = nodes()[0];
    (first || container).focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = nodes();
      if (items.length === 0) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  return containerRef;
}

export default useFocusTrap;